<template>
  <remote-modal
    ref="modal"
    modal-title="Destination select (Long Term)"
    @details-loaded="onFormLoaded"
  >
    <template #modal-content="{ modalState }">
      <template v-if="modalState.initialDataLoaded">
        <alert
          :message="errorMessage || undefined"
          :dismissable="true"
          @dismiss="errorMessage = null"
        />

        <div
          v-if="participantNameVisible"
          class="mb-2"
        >
          <label class="form-label">Participant</label>
          <input
            v-model="destination.participantName"
            type="text"
            class="form-control form-control-sm"
            readonly
          >
        </div>

        <template v-if="!ncpView">
          <div class="mb-2">
            <label class="form-label required">Destination choice</label>
            <select
              ref="destinationChoiceSelect"
              class="form-select"
            >
              <option />
            </select>
          </div>
        </template>
        <div
          class="mb-2"
        >
          <label class="form-label required">Duration</label>
          <select
            v-model="destination.durationId"
            class="form-select form-select-sm"
          >
            <option
              v-for="duration of selectedDestinationDurations"
              :key="duration.id"
              :value="duration.id"
            >
              {{ duration.name }}
            </option>
          </select>
        </div>
        <div class="row gx-2">
          <div class="mb-2 col">
            <label class="form-label">Start date</label>
            <date-picker
              v-model="startDateValue"
              :show-time="false"
              :small="true"
              data-date-ref="lt-start-date"
            />
          </div>
          <div class="mb-2 col">
            <label class="form-label">End date</label>
            <date-picker
              v-model="endDateValue"
              :show-time="false"
              :small="true"
              data-date-sync-ref="lt-start-date"
            />
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label">Notes</label>
          <textarea
            v-model="destination.notes"
            rows="4"
            class="form-control form-control-sm"
          />
        </div>
      </template>
    </template>
    <template #modal-footer="{ modalState }">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="approveBtnDisabled(modalState)"
        @click.prevent="approveParticipant"
      >
        {{ editMode ? 'Update' : 'Approve' }}
      </button>
    </template>
  </remote-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import $ from 'jquery'
import Jsona from 'jsona'
import axios from 'axios'
import Rails from '@rails/ujs'
import RemoteModal from '../remote-modal.vue'
import Alert from '../alert.vue'
import DatePicker from "../date-picker.vue"
import type { DataFormat, GroupedDataFormat } from 'select2'
import type { GridID } from '../types'
import { getAxiosError } from '@/js/axios-utils'
import { isNonEmptyString } from '@/js/js-utils'

interface LongTermDestinationChoiceDuration {
  id: number;
  name: string;
}

interface LongTermDestinationChoice {
  id: number;
  activityNumber: string;
  institutionName: string;
  durations: LongTermDestinationChoiceDuration[];
}

interface LongTermDestinationData {
  participantName: string | null;
  selectedChoiceId: number | null;
  selectedDurationId: number | null;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;

  primaryChoices: LongTermDestinationChoice[];
  otherChoices: LongTermDestinationChoice[];
  links: {
    submit: string;
  };
}

interface LongTermDestination {
  participantName: string | null;
  placeId: number | null;
  durationId: number | null;
  startDate: string | null;
  endDate: string | null;
  notes: string | null;
}

interface ModalState {
  initialDataLoaded: boolean;
  isLoading: boolean;
}

const emit = defineEmits<{
  "success": []
}>()

const modal = useTemplateRef<InstanceType<typeof RemoteModal>>("modal")
const destinationChoiceSelect = useTemplateRef<HTMLSelectElement>("destinationChoiceSelect")

const buildDestination = (): LongTermDestination => ({
  participantName: null,
  placeId: null,
  durationId: null,
  startDate: null,
  endDate: null,
  notes: null,
})

const ncpView = ref<boolean>(false)
const editMode = ref<boolean>(false)
const errorMessage = ref<string | null>(null)
const submitUrl = ref<string | null>(null)
const primaryChoices = ref<LongTermDestinationChoice[]>([])
const otherChoices = ref<LongTermDestinationChoice[]>([])
const destination = ref<LongTermDestination>(buildDestination())
const destinationSelectEventAttached = ref<boolean>(false)

const selectedDestination = computed((): LongTermDestinationChoice | null => {
  if (destination.value.placeId) {
    const selectedChoice = primaryChoices.value.find(choice => choice.id == destination.value.placeId)
    if (selectedChoice) {
      return selectedChoice
    } else {
      const otherSelectedChoice = otherChoices.value.find(choice => choice.id == destination.value.placeId)
      if (otherSelectedChoice) {
        return otherSelectedChoice
      }
    }
  }
  return null
})

const selectedDuration = computed((): LongTermDestinationChoiceDuration | null => {
  if (destination.value.durationId && selectedDestination.value) {
    const duration = selectedDestination.value.durations.find(duration => duration.id == destination.value.durationId)
    if (duration) {
      return duration
    }
  }
  return null
})

const selectedDestinationDurations = computed((): LongTermDestinationChoiceDuration[] => {
  return selectedDestination.value?.durations || []
})

const startDateValue = computed({
  get(): any {
    if (destination.value) {
      return {
        date: destination.value.startDate || '',
        time: null
      }
    }
    return { date: null, time: null }
  },
  set(value: any): void {
    if (destination.value) {
      if (value && isNonEmptyString(value.date)) {
        destination.value.startDate = value.date
      } else {
        destination.value.startDate = null
      }
    }
  }
})

const endDateValue = computed({
  get(): any {
    if (destination.value) {
      return {
        date: destination.value.endDate || '',
        time: null
      }
    }
    return { date: null, time: null }
  },
  set(value: any): void {
    if (destination.value) {
      if (value && isNonEmptyString(value.date)) {
        destination.value.endDate = value.date
      } else {
        destination.value.endDate = null
      }
    }
  }
})

const participantNameVisible = computed((): boolean => {
  return isNonEmptyString(destination.value.participantName)
})

const show = (url: string, applicationIds: GridID[] | null, isEditMode?: boolean): void => {
  errorMessage.value = null
  destinationSelectEventAttached.value = false
  editMode.value = isEditMode == true
  if (Array.isArray(applicationIds)) {
    modal.value?.showModal(url, {
      application_ids: applicationIds.join(',')
    }, {
      headers: {
        'Accept': 'application/vnd.api+json'
      },
    })
  } else {
    modal.value?.showModal(url)
  }
}

const hide = (): void => {
  modal.value?.hideModal()
}

const onFormLoaded = (data: any): void => {
  primaryChoices.value = []
  otherChoices.value = []
  ncpView.value = false

  destination.value = buildDestination()

  const jsdata = new Jsona().deserialize(data) as LongTermDestinationData | undefined
  if (jsdata && !Array.isArray(jsdata)) {
    if (isNonEmptyString(jsdata.links.submit)) {
      submitUrl.value = jsdata.links.submit
    }

    if (Array.isArray(jsdata.primaryChoices)) {
      primaryChoices.value = jsdata.primaryChoices
    }

    if (Array.isArray(jsdata.otherChoices)) {
      otherChoices.value = jsdata.otherChoices
    }

    destination.value.participantName = isNonEmptyString(jsdata.participantName) ? jsdata.participantName : null

    if (jsdata.selectedChoiceId) {
      destination.value.placeId = jsdata.selectedChoiceId
      if (jsdata.selectedDurationId) {
        destination.value.durationId = jsdata.selectedDurationId
      }
    }
    if (isNonEmptyString(jsdata.startDate)) {
      destination.value.startDate = jsdata.startDate
    }
    if (isNonEmptyString(jsdata.endDate)) {
      destination.value.endDate = jsdata.endDate
    }
    if (isNonEmptyString(jsdata.notes)) {
      destination.value.notes = jsdata.notes
    }
  }

  nextTick(() => {
    initFields()
  })
}

const initFields = (): void => {
  const destinationChoiceSelectEl = destinationChoiceSelect.value
  if (destinationChoiceSelectEl) {
    const data = [] as GroupedDataFormat[]

    const primaryItems = [] as DataFormat[]
    primaryChoices.value.forEach((choice) => {
      primaryItems.push({
        id: choice.id,
        text: `${choice.activityNumber} ${choice.institutionName}`,
      })
    })
    data.push({
      text: 'Primary choices',
      children: primaryItems
    })

    const otherItems = [] as DataFormat[]
    otherChoices.value.forEach((choice) => {
      otherItems.push({
        id: choice.id,
        text: `${choice.activityNumber} ${choice.institutionName}`,
      })
    })
    data.push({
      text: 'Other choices',
      children: otherItems
    })

    // clear selection box, otherwise previously populated items
    // are still preserved
    destinationChoiceSelectEl.innerHTML = ''
    destinationChoiceSelectEl.options.add(new Option('')) // required for placeholder to work

    $(destinationChoiceSelectEl).select2({
      placeholder: 'Select destination choice',
      allowClear: true,
      data: data,
      // templateResult: formatDestinationOption,
    })

    if (destination.value.placeId) {
      $(destinationChoiceSelectEl).val(destination.value.placeId)
      $(destinationChoiceSelectEl).trigger('change')
    }

    if (!destinationSelectEventAttached.value) {
      destinationSelectEventAttached.value = true
      $(destinationChoiceSelectEl).on('select2:select', (e: any) => {
        const result = e.params.data

        const selectedId = parseInt(result.id)
        if (!isNaN(selectedId)) {
          destination.value.placeId = selectedId
        } else {
          destination.value.placeId = null
        }
      })
    }
  }
}

const approveBtnDisabled = (modalState: ModalState): boolean => {
  if (!modalState.initialDataLoaded || modalState.isLoading) {
    return true
  }

  return selectedDestination.value == null || submitUrl.value == null
}

const approveParticipant = (): void => {
  errorMessage.value = null

  if (selectedDestination.value && selectedDuration.value && isNonEmptyString(submitUrl.value)) {
    modal.value?.showLoadingIndicator(true)
    const choiceParams: any = {
      place_id: selectedDestination.value.id,
      duration_id: selectedDuration.value.id,
      start_date: destination.value.startDate,
      end_date: destination.value.endDate,
      notes: destination.value.notes,
    }

    axios.post(submitUrl.value, {
      destination_choice: choiceParams
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken()
      },
    })
      .then(() => {
        emit('success')
      })
      .catch((error) => {
        errorMessage.value = getAxiosError(error) || 'An error occured'
      })
      .then(() => {
        modal.value?.showLoadingIndicator(false)
      })
  }
}

defineExpose({
  hide,
  show,
})
</script>
